import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import twilioService from "./services/twilioService";
import { getCurrentUserData } from "./apis/userApi";
import { FETCH_CURRENT_USER, SET_ACTIVE_ANNOUNCEMENT } from "./store/actions/actionTypes";
import { Loader } from "semantic-ui-react";
import Router from "./Router";
import UsersContextProvider from "./common/UsersContext";
import { AuthService } from "./services/authService";
import { CredentialsService } from "./services/credentialsService";
import socketService from "./services/socketService";
import apis from "./apis";
import { SOCKET_EVENTS } from "./constants/socketEvents";
import { FETCH_PRODUCT_CONFIG } from "./store/actions/productConfigActions";
import { getDomains } from "./apis/mailbox/mailbox.api";
import { mailboxActions } from "./store/reducers/mailboxReducer";
import { isDevAutoLoginEnabled, getDevAutoLoginUserId } from "./config/devAuth";
import toastr from "toastr";

const App = () => {
  const [isLoading, setIsLoading] = useState(true);

  const { loggedIn } = useSelector((state) => ({
    loggedIn: state.auth.loggedIn,
  }));
  const dispatch = useDispatch();

  useEffect(() => {
    if (loggedIn === false) {
      setIsLoading(false);
    }
    if (loggedIn) {
      setIsLoading(true);
      const getProductConfig = async () => {
        try {
          const {
            data: {
              aus,
              aus_v2,
              ds160,
              esta,
              eta,
              ind,
              nz,
              trk,
              usps,
              inv_letter,
              schengen,
              diversity,
              ge,
              sg,
              ds11,
              ds82,
              ds64,
              sl,
              tsa,
              sentri,
              nexus,
              quick_photo,
              china,
              ind_v2,
              schengen_v2,
              vn,
              egypt,
              uk,
              canada,
            },
          } = await apis.productConfig.fetchProductConfig();

          dispatch({
            type: FETCH_PRODUCT_CONFIG,
            payload: {
              aus,
              aus_v2,
              ds160,
              esta,
              eta,
              ind,
              nz,
              trk,
              usps,
              inv_letter,
              schengen,
              diversity,
              ge,
              sg,
              ds11,
              ds82,
              ds64,
              sl,
              tsa,
              sentri,
              nexus,
              quick_photo,
              china,
              ind_v2,
              schengen_v2,
              vn,
              egypt,
              uk,
              canada,
            },
          });
        } catch (error) {
          console.error(error);
        }

        try {
          const domainsResponse = await getDomains();
          dispatch(mailboxActions.setDomains(domainsResponse.data.domains));
        } catch (error) {
          console.error("Failed to load mailbox domains on startup:", error);
        }

        setIsLoading(false);
      };

      getProductConfig();
    }
  }, [loggedIn, dispatch]);

  useEffect(() => {
    async function fetchSimulatedUserData() {
      const simulatedUserId = CredentialsService.getUserId();
      if (CredentialsService.isSimulation()) {
        const { user } = await AuthService.simulateUserLogin(simulatedUserId);
        dispatch({
          type: FETCH_CURRENT_USER,
          payload: user,
        });
      }
    }
    fetchSimulatedUserData();
  }, [dispatch]);

  useEffect(() => {
    if (!CredentialsService.isSimulation()) {
      const init = async () => {
        try {
          const { data } = await getCurrentUserData();
          dispatch({
            type: FETCH_CURRENT_USER,
            payload: data,
          });
        } catch (err) {
          dispatch({
            type: FETCH_CURRENT_USER,
            payload: null,
          });
        }
      };
      if (loggedIn === undefined) {
        init();
      }
    }
  }, [dispatch, loggedIn]);

  useEffect(() => {
    if (loggedIn) {
      socketService.connect();
      twilioService.loadToken();
      return () => {
        socketService.disconnect();
      };
    }
  }, [loggedIn]);

  useEffect(() => {
    if (!loggedIn) return;

    const fetchActiveAnnouncement = async () => {
      try {
        const { data } = await apis.announcements.getActiveAnnouncement();
        dispatch({ type: SET_ACTIVE_ANNOUNCEMENT, payload: data.announcement });
      } catch (err) {
        console.error("Failed to fetch active announcement:", err);
      }
    };

    fetchActiveAnnouncement();

    const subscription = socketService.socketSubject.subscribe(({ type }) => {
      if (type === SOCKET_EVENTS.ANNOUNCEMENTS.UPDATED) {
        fetchActiveAnnouncement();
      }
    });

    return () => subscription.unsubscribe();
  }, [loggedIn, dispatch]);

  // Dev auto-login effect
  useEffect(() => {
    async function performDevAutoLogin() {
      if (!isDevAutoLoginEnabled()) {
        return;
      }
      if (loggedIn !== false) {
        return;
      }
      // Skip if simulation mode is active (URL param takes precedence)
      if (CredentialsService.isSimulation()) {
        return;
      }
      const userId = getDevAutoLoginUserId();
      if (!userId) {
        return;
      }
      try {
        const { user } = await AuthService.devAutoLogin(userId);
        dispatch({
          type: FETCH_CURRENT_USER,
          payload: user,
        });
      } catch (err) {
        console.error("Dev auto-login failed:", err);
        toastr.error(
          `Dev auto-login failed: ${err.message || "Unknown error"}. Check console for details.`,
          "Auto-Login Error",
          { timeOut: 0, extendedTimeOut: 0 }
        );
      }
    }
    performDevAutoLogin();
  }, [loggedIn, dispatch]);

  if (loggedIn === undefined || isLoading) {
    return <Loader active />;
  }

  return (
    <UsersContextProvider>
      <Router />
    </UsersContextProvider>
  );
};

export default App;
